import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import TagsService, { type CreateTagAssignmentInput, type GetTagAssignmentsParams } from '~/services/TagsService';

export function useGetTagAssignments(params?: GetTagAssignmentsParams, enabled = true) {
  return useQuery({
    queryKey: ['tag-assignments', params?.tagId, params?.offset, params?.limit],
    queryFn: () => {
      if (!params) {
        throw new Error('params is required');
      }
      return TagsService.getTagAssignments(params);
    },
    enabled: enabled && !!params,
  });
}

export function useCreateTagAssignments() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (assignments: Array<CreateTagAssignmentInput>) => {
      const results = [];
      for (const data of assignments) {
        const result = await TagsService.createTagAssignment(data);
        results.push(result);
      }
      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tag-assignments'] });
      enqueueSnackbar(`${variables.length} assignment(s) created successfully`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating assignments: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useDeleteTagAssignment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (assignmentId: string) => TagsService.deleteTagAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tag-assignments'] });
      enqueueSnackbar('Assignment deleted successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error deleting assignment: ${error.message}`, { variant: 'error' });
    },
  });
}
